import Button from '@rio-cloud/rio-uikit/Button';
import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import NoData from '@rio-cloud/rio-uikit/NoData';
import { useIntl } from 'react-intl';
import { useGetDeviceSharingsQuery } from '../../../services/assetsApi';
import type { Asset } from './types';

type Props = {
    asset: Asset;
    onShowDetails: (asset: Asset) => void;
};

const getSharedAccountCount = (sharedAccountIds: string[]) => new Set(sharedAccountIds).size;

export const AssetSharingCell = ({ asset, onShowDetails }: Props) => {
    const intl = useIntl();
    const { data: deviceSharings, isError, isLoading } = useGetDeviceSharingsQuery();
    const assetSharings = deviceSharings?.filter(sharing => sharing.asset_id === asset.id);

    if (isLoading) {
        return <ContentLoader height={14} width={96} />;
    }

    if (isError || !assetSharings) {
        return (
            <Button
                bsSize={Button.XS}
                variant={Button.VARIANT_LINK}
                onClick={event => {
                    event.stopPropagation();
                    onShowDetails(asset);
                }}
            >
                {intl.formatMessage({ id: 'intl-msg:sharing.error.headline' })}
            </Button>
        );
    }

    const sharedAccountCount = getSharedAccountCount(assetSharings.map(sharing => sharing.shared_account_id));

    if (sharedAccountCount === 0) {
        return <NoData text={intl.formatMessage({ id: 'intl-msg:sharing.accounts' }, { count: 0 })} />;
    }

    return (
        <Button
            bsSize={Button.XS}
            variant={Button.VARIANT_LINK}
            data-testid='asset-sharing-details-button'
            onClick={event => {
                event.stopPropagation();
                onShowDetails(asset);
            }}
        >
            {intl.formatMessage({ id: 'intl-msg:sharing.accounts' }, { count: sharedAccountCount })}
        </Button>
    );
};
